import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Typography,
  IconButton,
  TextField,
  Button,
  Box,
} from "@material-ui/core";
import { Rating } from "@material-ui/lab";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

const API_BASE = "https://printspaceai.com/api/v1/product-reviews";

const ProductReviewSection = ({ blueprintId, shopDesignId }) => {
  const [reviews, setReviews] = useState([]);
  const [userReview, setUserReviews] = useState({});
  const [form, setForm] = useState({ rating: 0, comment: "" });
  const [editingReview, setEditingReview] = useState(null);

  // When shopDesignId is set, reviews are design-specific (shop). Otherwise blueprint-specific (catalog).
  useEffect(() => {
    if (shopDesignId) {
      fetchReviews();
    } else if (blueprintId != null && blueprintId !== "" && !Number.isNaN(Number(blueprintId))) {
      fetchReviews();
    } else {
      setReviews([]);
      setUserReviews(null);
    }
  }, [blueprintId, shopDesignId]);

  let userId = "";

  const username = JSON.parse(localStorage?.getItem("userData") || "{}");

  if (username?.id_token) {
    const decoded = jwtDecode(username?.id_token); // Decode JWT

    userId = decoded?.user_id;
  }

  const fetchReviews = async () => {
    if (shopDesignId) {
      try {
        const res = await axios.get(
          `${API_BASE}/shop-design/${shopDesignId}?page=1&page_size=20`
        );
        const allReviews = res?.data?.data?.reviews || [];
        if (userId && allReviews.length) {
          const own = allReviews.find((r) => String(r.user_id) === String(userId));
          setUserReviews(own || null);
          setReviews(allReviews.filter((r) => String(r.user_id) !== String(userId)));
        } else {
          setReviews(allReviews);
          setUserReviews(null);
        }
      } catch (err) {
        console.error("Failed to fetch shop design reviews:", err);
        setReviews([]);
        setUserReviews(null);
      }
      return;
    }
    if (blueprintId == null || blueprintId === "" || Number.isNaN(Number(blueprintId))) return;
    try {
      const res = await axios.get(
        `${API_BASE}/blueprint/${Number(blueprintId)}?page=1&page_size=20`
      );
      if (userId && res?.data?.data?.reviews) {
        const allReviews = res.data.data.reviews;
        const userReview = allReviews.find((review) => review.user_id === userId);
        const otherReviews = allReviews.filter((review) => review.user_id !== userId);
        setUserReviews(userReview || null);
        setReviews(otherReviews);
      } else {
        setReviews(res?.data?.data?.reviews || []);
        setUserReviews(null);
      }
    } catch (err) {
      console.error("Failed to fetch reviews:", err);
    }
  };
  const handleAddOrUpdate = async () => {
    // Check if user is logged in
    if (!userId) {
      toast.error("Please log in to submit a review");
      return;
    }

    if (!form?.rating) {
      toast.error("Star Rating required");
      return;
    }

    if (!form?.comment) {
      toast.error("Comment required");
      return;
    }
    try {
      if (editingReview) {
        // Update review
        await axios.put(`${API_BASE}/${editingReview?.product_review_id}`, {
          rating: form.rating,
          comment: form.comment,
          media_urls: [], // Add any media if needed
        });
        toast.success("Review Successfully updated");
      } else {
        const payload = {
          user_id: userId,
          blueprint_id: Number(blueprintId) || 0,
          rating: form.rating,
          comment: form.comment,
        };
        if (shopDesignId) {
          payload.shop_design_id = shopDesignId;
        }
        await axios.post(API_BASE, payload, {
          headers: { "Content-Type": "application/json" },
        });
        toast.success("Review Successfully added");
      }
      fetchReviews(); // Refresh the reviews
      setForm({ rating: 0, comment: "" });
      setEditingReview(null);
    } catch (err) {
      console.error("Failed to submit review:", err);
      console.error("Failed to submit review:", err);

      // Show specific error if available from backend response
      const message =
        err.response?.data?.message ||
        "Something went wrong while submitting the review.";
      toast.error(message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE}/${id}`);
      fetchReviews();
    } catch (err) {
      console.error("Failed to delete review:", err);
    }
  };

  //   const handleEdit = (review) => {
  //     setEditingReview(review);
  //     setForm({ rating: review.rating, comment: review.comment });
  //   };

  const isValidBlueprint = blueprintId != null && blueprintId !== "" && !Number.isNaN(Number(blueprintId));
  const isValidShopDesign = shopDesignId != null && shopDesignId !== "";

  // Need either a valid shop design (shop page) or blueprint (catalog) to show reviews
  if (!isValidShopDesign && !isValidBlueprint) {
    return (
      <Box p={2} style={{ maxWidth: 800 }}>
        <Typography variant="body2" color="textSecondary">
          No reviews for this product.
        </Typography>
      </Box>
    );
  }

  // Show reviews for all users, but only allow logged-in users to add/edit reviews
  if (!userId) {
    return (
      <Box p={2} style={{ maxWidth: 800 }}>
        <Typography variant="h4" gutterBottom style={{ fontWeight: 600, fontSize: 18 }}>
          Product Reviews
        </Typography>
        
        <Box mb={4}>
          <Typography variant="body1" color="textSecondary">
            Please log in to add a review or see your personal review.
          </Typography>
        </Box>
        
        {/* Show other reviews even for guest users */}
        {reviews?.map((review) => (
          <Card key={review.id} style={{ marginBottom: 20 }}>
            <CardHeader
              avatar={<Avatar>{review?.username?.charAt(0)}</Avatar>}
              title={
                <Typography variant="h6" style={{ fontSize: 16 }}>
                  {review?.username || "User"}
                </Typography>
              }
              subheader={
                <Typography variant="body1" style={{ fontSize: 12 }}>
                  {review.date || ""}
                </Typography>
              }
            />
            <CardContent>
              <Rating value={review.rating} readOnly size="large" />
              <Typography variant="body1" style={{ fontSize: 14, marginTop: 8 }}>
                {review.comment}
              </Typography>
            </CardContent>
          </Card>
        ))}
        
        {(!reviews || reviews.length === 0) && (
          <Typography variant="body1" color="textSecondary" style={{ textAlign: 'center', padding: '20px' }}>
            No reviews yet. Be the first to review this product!
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box p={2} style={{ maxWidth: 800 }}>
      <Typography variant="h4" gutterBottom style={{ fontWeight: 600, fontSize: 18 }}>
        Product Reviews
      </Typography>

      <Box mb={4}>
        <Typography variant="h5" gutterBottom>
          {editingReview ? "Edit Review" : "Add a Review"}
        </Typography>

        <Rating
          name="rating"
          value={form.rating}
          onChange={(e, newVal) =>
            setForm((prev) => ({ ...prev, rating: newVal }))
          }
          size="large"
          style={{
            transform: "scale(1.4)",
            marginLeft: "10px",
            marginTop: "10px",
          }}
        />

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Your Review"
          variant="outlined"
          value={form.comment}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, comment: e.target.value }))
          }
          style={{ marginTop: 16 }}
          InputProps={{ style: { fontSize: 14 } }}
          InputLabelProps={{ style: { fontSize: 14 } }}
        />

        <Button
          variant="contained"
          color="primary"
          size="small"
          style={{ marginTop: 16, fontSize: 14, backgroundColor: "#4087a8" }}
          onClick={handleAddOrUpdate}
        >
          {editingReview ? "Update Review" : "Submit Review"}
        </Button>
      </Box>

      {userReview?.username ? (
        <Card key={userReview} style={{ marginBottom: 20 }}>
          <CardHeader
            avatar={<Avatar>{userReview?.username?.charAt(0)}</Avatar>}
            title={
              <Typography variant="h6" style={{ fontSize: 16 }}>
                {userReview?.username || "User"}
              </Typography>
            }
            subheader={
              <Typography variant="body1" style={{ fontSize: 12 }}>
                {userReview?.date || ""}
              </Typography>
            }
            action={
              <>
                <IconButton
                  onClick={() => {
                    setEditingReview(userReview);
                    setForm({
                      rating: userReview.rating,
                      comment: userReview.comment,
                    });
                    setEditingReview(userReview);
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleDelete(userReview?.product_review_id)}
                >
                  <DeleteIcon />
                </IconButton>
              </>
            }
          />
          <CardContent>
            {console.log("userReview?.rating===", userReview)}
            {userReview?.rating ? (
              <Rating value={userReview?.rating} readOnly size="large" />
            ) : null}
            <Typography variant="body1" style={{ fontSize: 14, marginTop: 8 }}>
              {userReview?.comment}
            </Typography>
          </CardContent>
        </Card>
      ) : null}
      {reviews?.map((review) => (
        <Card key={review.id} style={{ marginBottom: 20 }}>
          <CardHeader
            avatar={<Avatar>{review?.username?.charAt(0)}</Avatar>}
            title={
              <Typography variant="h6" style={{ fontSize: 16 }}>
                {review?.username || "User"}
              </Typography>
            }
            subheader={
              <Typography variant="body1" style={{ fontSize: 12 }}>
                {review.date || ""}
              </Typography>
            }
            // action={
            //   <>
            //     <IconButton onClick={() => handleEdit(review)}>
            //       <EditIcon />
            //     </IconButton>
            //     <IconButton onClick={() => handleDelete(review.product_review_id)}>
            //       <DeleteIcon />
            //     </IconButton>
            //   </>
            // }
          />
          <CardContent>
            <Rating value={review.rating} readOnly size="large" />
            <Typography variant="body1" style={{ fontSize: 14, marginTop: 8 }}>
              {review.comment}
            </Typography>
          </CardContent>
        </Card>
      ))}
      
      {(!reviews || reviews.length === 0) && (
        <Typography variant="body1" color="textSecondary" style={{ textAlign: 'center', padding: '20px' }}>
          No reviews yet. Be the first to review this product!
        </Typography>
      )}
    </Box>
  );
};

export default ProductReviewSection;
